import { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';

import moment from 'moment';
import PropTypes from 'prop-types';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { getReservationStatusOptions } from 'shared/constants';
import { useTransl } from 'shared/lib/i18n';

import AsyncTableContainer from '../Table/AsyncTableContainer';
import ModalContainer from './ModalContainer';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';
import ModalHandlerComponent from '../Modal/ModalHandlerComponent';

import {
  checkHasPermissions,
  isAdmin,
  isPM,
  isSuperOrConcierge,
} from '../../utils';
import { getLinkToUser } from '../Messages/utils';
import { contactPersonRole } from '../Person/utils';
import { getExportTitle } from '../ViewExport/utils';
import { getAmenityBaseUrl, getAmenityReservationTime } from './utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';

import permissions from '../../permissions';
import { DATE_RANGES_TYPES, TABLE_DATA_TYPES } from '../../staticData';
import { history } from '../../store';
import DropDownMenu from '../DropDownMenu';
import WithLink from '../Linkify';
import StyledLink from '../StyledLink';
import CellPersonWithImage from '../Table/CellPersonWithImage';
import ListActions from '../Table/ListActions';
import {
  amenityFilter,
  amenityMultiStatusFilter,
  buildingFilter,
  limitFilter,
} from '../Table/filters';
import Tooltip from '../Tooltip';
import AmenityReservationsExport from './AmenityReservationsExport';

const AmenityReservationsComponent = (props) => {
  const { t } = useTransl();
  const theme = useTheme();
  const { url } = useRouteMatch();
  const baseUrl = getAmenityBaseUrl(url);
  const {
    pmcId,
    buildingId,
    pmc,
    building,
    items,
    getItems,
    listKey,
    listParams,
    buildings,
    amenities,
    onLoad,
    // onRemoveReservation,
    preselectedAmenityId,
    onAmenityReservationsExport,
    isCalendarView,
    match,
    onAmenityStatusChange,
    isReservationHistory,
  } = props;
  const [open, setOpen] = useState(false);
  const [rowInfo, setRowInfo] = useState();

  const handleOpen = (row) => {
    setOpen(true);
    setRowInfo(row);
  };

  const handleClose = () => setOpen(false);

  const columns = [
    {
      Header: t('title.building'),
      id: 'building',
      accessor: ({
        amenity: {
          building: { id, name },
        },
      }) => <StyledLink to={`/building/${id}`}>{name}</StyledLink>,
      headerClassName: 'sort',
      show: !preselectedAmenityId,
    },
    {
      Header: t('title.amenity'),
      id: 'name',
      accessor: ({ amenity: { name, images } }) => (
        <Box display="flex" alignItems="center">
          {images?.[0] && (
            <Box mr="1rem">
              <ExpandableImageComponent
                photo={images[0]}
                caption={name}
                height="3.2rem"
                isSquare
              />
            </Box>
          )}
          <Typography>{name}</Typography>
        </Box>
      ),
      sortable: false,
      show: !preselectedAmenityId,
    },
    {
      Header: t('title.reservationTime'),
      id: 'start_datetime',
      accessor: ({ date, startTime, endTime, amenity }) => {
        const { displayDate, isSameDay } = getAmenityReservationTime(
          date?.slice(0, -6),
          startTime,
          endTime,
          amenity,
        );
        if (!isSameDay) {
          return (
            <Tooltip tooltip={t('amenity.periodNote')}>{displayDate}</Tooltip>
          );
        }
        return displayDate;
      },
      headerClassName: 'sort',
    },
    {
      Header: t('title.reservedBy'),
      id: 'contactPerson',
      sortable: false,
      accessor: ({ contactPerson }) => (
        <CellPersonWithImage
          person={contactPerson}
          linkUrl={getLinkToUser(contactPerson, true)}
        />
      ),
    },
    {
      Header: t('title.role'),
      id: 'role',
      sortable: false,
      accessor: ({ contactPerson = {} }) => contactPersonRole(contactPerson),
    },
    {
      Header: t('title.comment'),
      id: 'comments',
      accessor: ({ comments }) => <WithLink>{comments}</WithLink>,
      sortable: false,
    },
    {
      Header: t('title.status'),
      id: 'status',
      fixed: 'right',
      width: 140,
      accessor: ({ status, id }) => {
        const { status: currentStatus = 'approved' } = status || {};

        const isRoleHasApprovalPermission =
          checkHasPermissions(permissions.approve_amenity_reservation) &&
          !isAdmin();

        const menuItems = [
          {
            label: t('actions.cancel'),
            onClick: () => onAmenityStatusChange(id, { status: 'cancelled' }),
          },
        ];

        if (isRoleHasApprovalPermission) {
          menuItems.push({
            label: t('actions.approve'),
            onClick: () => onAmenityStatusChange(id, { status: 'approved' }),
          });
          menuItems.push({
            label: t('actions.deny'),
            onClick: () => onAmenityStatusChange(id, { status: 'denied' }),
          });
        }

        const color =
          currentStatus === 'approved'
            ? theme.palette.success.main
            : currentStatus === 'pending'
            ? theme.palette.custom.pendingStatus
            : currentStatus === 'cancelled'
            ? theme.palette.custom.notificationTime
            : theme.palette.secondary.main;

        const menuTitle = (
          <Grid
            container
            direction="row"
            wrap="nowrap"
            alignItems="center"
            justifyContent="center"
            role="presentation"
          >
            {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1) ??
              t('amenity.approved')}
            <ArrowDropDownIcon role="presentation" fontSize="medium" />
          </Grid>
        );

        return (
          <DropDownMenu
            items={menuItems}
            title={menuTitle}
            color={color}
            isButton
            disabled={isAdmin()}
          />
        );
      },
      sortable: true,
    },
    // {
    //   Header: t('title.actions'),
    //   id: 'actions',
    //   fixed: 'right',
    //   accessor: ({ id }) => {
    //     const action = [
    //       {
    //         label: t('actions.delete'),
    //         isFullWidth: true,
    //         onClick: () => onRemoveReservation({ id, listParams }),
    //         color: theme.palette.secondary.main,
    //       },
    //     ];
    //     return <ListActions actions={action} isFullWidth />;
    //   },
    //   width: 120,
    //   sortable: false,
    //   show: isAdmin() || isPM(match?.params),
    // },
  ];

  const dateRangeTypeOptions = [
    DATE_RANGES_TYPES.date,
    DATE_RANGES_TYPES.created_at,
  ];

  const filters = [
    buildingFilter(listParams, buildings),
    amenityFilter(listParams, amenities),
    limitFilter(listParams),
    amenityMultiStatusFilter(listParams, amenities),
  ];

  if (isCalendarView) {
    filters.splice(2, 2);
  }

  if (buildingId) {
    filters.splice(0, 1);
  }

  const action = [
    {
      label: t('amenity.addReservation'),
      url: `${baseUrl}/reservations/create`,
      show:
        isAdmin() || isPM(match?.params) || isSuperOrConcierge(match?.params),
    },
  ];

  const onEditReservation = (reservationId) =>
    reservationId && history.push(`${baseUrl}/reservations/${reservationId}`);

  const rowStyles = {
    style: {
      cursor: 'pointer',
    },
  };

  const defaultParams = {
    ordering: '-start_datetime',
    building: buildingId,
    pmc: pmcId,
    amenity: preselectedAmenityId,
    date_after: isReservationHistory ? null : moment().startOf('day').format(),
  };

  const tableDataType = TABLE_DATA_TYPES.items;
  const title = preselectedAmenityId
    ? t('title.amenityDetails')
    : t('title.amenityReservations');

  const exportTitle = getExportTitle(title, pmc, building);
  const exportActions = (
    <AmenityReservationsExport
      title={exportTitle}
      getDataForExport={onAmenityReservationsExport}
      columns={columns}
    />
  );

  return (
    <>
      {open && (
        <ModalHandlerComponent
          modal={{
            modalType: 'CUSTOM',
            modalProps: {
              title: `${t('title.reservationDetails')} - ${
                rowInfo?.original?.amenity?.name
              }`,
              maxWidth: 'md',
            },
          }}
          onModalClose={handleClose}
        >
          <ModalContainer
            handleClose={handleClose}
            reservation={rowInfo?.original}
            options={getReservationStatusOptions()}
            onEditReservation={onEditReservation}
          />
        </ModalHandlerComponent>
      )}

      <AsyncTableContainer
        tableId={TABLE_ID.amenityReservations}
        title={title}
        items={items}
        columns={columns}
        defaultSort={TABLE_CONSTANTS.start_datetime}
        defaultSortDesc={false}
        manual
        filters={filters}
        onLoad={onLoad}
        getItems={getItems}
        listParams={listParams}
        defaultParams={defaultParams}
        tableDataType={tableDataType}
        listKey={listKey}
        action={<ListActions actions={action} />}
        dateRangeType={DATE_RANGES_TYPES.created_at}
        dateRangeTypeOptions={!isCalendarView && dateRangeTypeOptions}
        onRowClick={handleOpen}
        rowStyles={rowStyles}
        datePicker={!isCalendarView}
        hideRecordsInfo={!!preselectedAmenityId}
        showTableRowsOnly={!!preselectedAmenityId}
        showFiltersAreApplied={false}
        exportActions={exportActions}
        isCalendarView={isCalendarView}
        searchAutoFocus={!isCalendarView}
      />
    </>
  );
};

AmenityReservationsComponent.defaultProps = {
  pmcId: null,
  buildingId: null,
  pmc: null,
  building: null,
  buildings: {},
  amenities: [],
  preselectedAmenityId: null,
  isCalendarView: false,
};

AmenityReservationsComponent.propTypes = {
  pmcId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  buildingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pmc: PropTypes.instanceOf(Object),
  building: PropTypes.instanceOf(Object),
  items: PropTypes.instanceOf(Object).isRequired,
  getItems: PropTypes.func.isRequired,
  listKey: PropTypes.string.isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  buildings: PropTypes.instanceOf(Object),
  amenities: PropTypes.instanceOf(Array),
  onLoad: PropTypes.func.isRequired,
  // onRemoveReservation: PropTypes.func.isRequired,
  preselectedAmenityId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  isCalendarView: PropTypes.bool,
  isReservationHistory: PropTypes.bool,
  onAmenityReservationsExport: PropTypes.func.isRequired,
  match: PropTypes.instanceOf(Object).isRequired,
  onAmenityStatusChange: PropTypes.func.isRequired,
};

export default AmenityReservationsComponent;
